<template>
    <div :class="{ relative: isStoreDesigner }">
        <venue-header-new v-if="hasNewHeader" />
        <VenueHeader v-else />
        <MenuStickyHeader
            ref="menuStickyHeader"
            :has-new-header="hasNewHeader"
            :showBasket="showBasket"
            @search="updateSearchQuery"
        />

        <main
            v-if="!menuIsLoading && categories"
            :class="{
                'pt-12': hasNewHeader,
                'visual-menu': visualMenu,
                'happy-endings': isHappyEndings,
                'pizza-pilgrims': isPizzaPilgrims,
                'custom-menu-background-image': isMenuBgImage
            }"
            class="menu-page"
        >
            <div
                class="menu-page__menu-wrapper flew-row container-lg flex flex-wrap items-start justify-start px-2 py-3 md:py-6 lg:px-3"
            >
                <div
                    v-if="!enabled"
                    id="busyMessage"
                    class="store-notice break-words bg-red-50 px-3 py-3 shadow-none sm:mb-4 sm:rounded-sm sm:px-4 sm:shadow-inner"
                >
                    <div class="flex">
                        <p
                            class="text-sm font-medium leading-5 text-red-800"
                            v-html="busyMessage"
                        />
                    </div>
                </div>

                <div
                    v-if="allergenDisabled"
                    id="allergenClosed"
                    class="bg-page store-notice cursor-pointer break-words px-3 py-3 shadow-none sm:mb-4 sm:rounded-sm sm:px-4 sm:shadow-inner"
                    @click="$modal.show('allergen-modal')"
                >
                    <div class="">
                        <p class="text-sm font-medium leading-5 text-red-800">
                            {{ $t('allergenOrderDisabledMessage.reason') }}
                        </p>
                        <p class="text-gray-600 mt-1 text-xs">
                            {{
                                $t('allergenOrderDisabledMessage.changeAnswer')
                            }}
                        </p>
                    </div>
                </div>

                <StoreNotice
                    v-if="!search && notices && notices.length"
                    class="store-notice"
                    location="menu"
                />

                <MenuOffers
                    v-if="!search && offers && offers.length"
                    :offers="offers"
                    @showItemModalById="showItemModalById"
                />

                <dietary-filters
                    v-if="hasCateringFulfillmentMethodModal"
                    :showIcons="true"
                    class="border-button-primary bg-secondary menu-page__menu-category pt-2 rounded-md"
                    size="large"
                >
                    <h6 class="text-sm text-primary mb-1">{{
                        $t('menu.dietaryFilters')
                    }}</h6>
                </dietary-filters>

                <popular-products
                    v-if="hasPopularItemsCategory && popularProducts.length"
                    :products="popularProducts"
                    @show-modal="showItemModal"
                />

                <template v-if="customSections?.length">
                    <div
                        v-for="(section, index) in customSections"
                        :key="`custom-section-${index}`"
                        :style="{
                            background: section.sectionBg || 'none',
                            padding: section.padding || '10px 10px',
                            'justify-content':
                                section.verticalAlign || 'flex-start'
                        }"
                        class="custom-section bg-red-300 h-[400px] w-full rounded-md flex flex-col"
                        v-html="section.content || ''"
                    />
                </template>

                <!--                <menu-recommendations-->
                <!--                    location="menu"-->
                <!--                    :instance-id="'menu'"-->
                <!--                    :current-item="null"-->
                <!--                    class="mt-8 px-4 menu-page__menu-category"-->
                <!--                    @showItemModal="showItemModal"-->
                <!--                />-->

                <category-grid
                    v-if="shouldShowCategoryGrid"
                    :categories="navCategories"
                    @category-click="onCategoryClick"
                />

                <div
                    v-if="isOnCategoryPage && currentCategory"
                    class="menu-page__menu-category w-full"
                >
                    <button
                        class="flex items-center gap-2 px-2 py-3 text-sm font-medium text-primary hover:text-heading transition-colors"
                        @click="goBackToCategories"
                    >
                        <back-icon />
                        {{ $t('categoryGrid.backToCategories') }}
                    </button>
                </div>

                <div
                    v-for="(category, index) in displayCategories"
                    v-show="!shouldShowCategoryGrid"
                    :id="'category-' + category.id"
                    :key="category.id"
                    class="menu-page__menu-category"
                >
                    <template v-if="category.image && !hasCategoryHeaderImages">
                        <img
                            :alt="category.name"
                            :class="{
                                'mb-2 mt-4': largeImages,
                                '-mb-2': !largeImages
                            }"
                            :src="`${category.image}-/format/auto/-/resize/1500x/`"
                            class="select-none"
                        />
                    </template>
                    <template v-else-if="isBaoDineIn">
                        <div class="px-2 pb-3.5 pt-5 flex bg-page -mt-px">
                            <h3
                                class="font-heading bg-button px-4 py-2 text-base leading-none font-bold text-white"
                                style="background-color: #151515"
                                >{{ category.name }}</h3
                            >
                        </div>
                    </template>
                    <template
                        v-else-if="
                            accountId === '5f4e9ddc-3431-43f5-9de0-7aa356564afc'
                        "
                    >
                        <div class="bg-white px-5 pb-5 pt-10">
                            <h3
                                class="font-heading text-xl leading-none font-bold text-category-heading"
                                >{{ category.name }}</h3
                            >
                        </div>
                    </template>
                    <template v-else>
                        <div
                            class="menu-category__heading flex items-end justify-between pb-2 pt-12"
                        >
                            <h2
                                :class="{ '!text-6xl': isGetBaked }"
                                class="font-heading text-2xl font-bold text-category-heading"
                            >
                                {{ category.name }}
                            </h2>
                            <span
                                v-if="!!displayCategoryDiscount(category.id)"
                                class="inline-flex mb-0.5 flex-shrink-0 items-center clip-path-button rounded-button rounded-md bg-button font-button px-2.5 py-0.5 text-sm font-medium text-white"
                            >
                                {{ displayCategoryDiscount(category.id) }}
                            </span>
                        </div>
                    </template>

                    <p
                        v-if="!category.image && category.description"
                        class="pb-2 text-sm text-secondary whitespace-pre-line"
                    >
                        {{ category.description }}
                    </p>
                    <ul
                        class="menu-page__items grid lg:gap-x-3 list-none pt-2"
                        v-bind="
                            isStoreDesigner ? { key: showCurrencySymbol } : {}
                        "
                    >
                        <menu-item
                            v-for="(item, itemIndex) in orderMenuItems(
                                category.products
                            )"
                            :key="`${item.id}${category.id}${itemIndex}`"
                            :category-index="index"
                            :image-size="imageSize || 'md'"
                            :isAvailable="isInStock(item)"
                            :item="item"
                            :item-index="itemIndex"
                            :large-images="largeImages"
                            :quick-add="hasQuickAddToCart"
                            @click.native="showItemModal(item)"
                        ></menu-item>
                    </ul>

                    <StoriesContainer
                        v-if="index === 0 && !search && hasStoriesEnabled"
                        @cta-action="handleStoryCtaAction"
                    />
                </div>

                <div
                    v-if="filteredCategories.length === 0"
                    class="menu-page__menu-category px-2 lg:px-3"
                >
                    <template v-if="hasActiveFilters">
                        <h1
                            class="pb-2 pt-24 text-lg text-center font-bold text-primary"
                        >
                            {{ $t('menu.nothingWithFilters') }}
                        </h1>
                        <!--                        <div class="text-center">-->
                        <!--                            <span-->
                        <!--                                class="cursor-pointer font-heading text-lg text-heading underline"-->
                        <!--                                @click="clearAllFilters"-->
                        <!--                            >-->
                        <!--                                {{ $t('menu.clearAllFilters') }}-->
                        <!--                            </span>-->
                        <!--                        </div>-->
                    </template>
                    <template v-else>
                        <h1
                            class="pb-2 pt-24 text-lg text-center font-bold text-gray-900"
                        >
                            {{ $t('menu.nothingOnMenu') }}
                        </h1>
                    </template>
                </div>
                <div
                    v-if="search && categoriesSearch.length === 0"
                    class="menu-page__menu-category px-2 text-center lg:px-3"
                    @click="resetSearchQuery"
                >
                    <h1
                        class="mb-4 mt-12 cursor-pointer font-heading text-lg font-bold text-heading sm:text-xl"
                    >
                        {{ $t('menu.noMoreResults') }}
                    </h1>
                    <span
                        class="cursor-pointer font-heading text-lg text-heading underline"
                    >
                        {{ $t('menu.noMoreResultsReset') }}
                    </span>
                </div>

                <div
                    v-if="isOnCategoryPage && currentCategory"
                    class="menu-page__menu-category w-full"
                >
                    <button
                        class="flex items-center gap-2 px-2 py-3 text-sm font-medium text-primary hover:text-heading transition-colors"
                        @click="goBackToCategories"
                    >
                        <back-icon />
                        {{ $t('categoryGrid.backToCategories') }}
                    </button>
                </div>
                <menu-notice
                    id="menuNotice"
                    v-bind="isStoreDesigner ? { key: menuNotice } : {}"
                />
            </div>
        </main>

        <main
            v-else-if="menuIsLoading"
            :class="{ 'pt-12': hasNewHeader }"
            class="menu-page"
        >
            <div
                class="menu-page__menu-wrapper flew-row container-lg flex flex-wrap items-start justify-start px-2 py-3 md:py-6 lg:px-3"
            >
                <MenuOffersSkeleton v-if="offers && offers.length > 0" />
                <div
                    v-for="(n, index) in 2"
                    :key="index"
                    class="menu-page__menu-category pt-7 sm:pt-10"
                >
                    <menu-category-skeleton class="mb-3 px-4 sm:mb-0 sm:px-0" />
                    <ul
                        class="menu-page__items grid lg:gap-x-3 list-none sm:pt-2"
                    >
                        <div
                            v-for="(n, index) in 5"
                            :key="index"
                            ref="menuItemSkeleton"
                            class="menu-page__item relative mb-4 block select-none rounded-sm bg-secondary px-0 py-0 outline-none lg:py-2"
                        >
                            <menu-item-skeleton />
                        </div>
                    </ul>
                </div>
            </div>
        </main>

        <cross-sell-modal v-if="categories" :products="crossSellProducts" />
        <venue-notice-confirmation-modal
            @confirm="onConfirmModalBeforeCheckout"
            @showModalBeforeCheckout="showModalBeforeCheckout = true"
        />
        <item-modal />
        <rewards-modal />
        <menu-summary-modal
            :categories="categories"
            @scroll="scrollToElement"
        />
        <allergen-modal />
        <modifier-extra-info-modal />
        <allergen-filters-modal />

        <!--        <div-->
        <!--            v-if="isStoreDesigner"-->
        <!--            class="fixed top-0 left-0 bg-transparent h-full w-full z-50"-->
        <!--        />-->
    </div>
</template>

<script>
import ably from '@/helpers/ably';
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
import { getRWGToken } from '@/helpers/rwgTokenHelper';
import { orderBy } from '@/util/sort';
import {
    DiscountTypesEnum,
    HeaderStylesEnum,
    CategoryRoutingEnum
} from '@/enums';
import {
    createSearchInstance,
    performSearch,
    processSearchResults,
    trackSearchAnalytics
} from '@/services/search.service.js';

import VenueHeader from '@/components/VenueHeader';
import MenuStickyHeader from '@/components/MenuStickyHeader';
import MenuItem from '@/components/MenuItem';
import StoreNotice from '@/components/StoreNotice';
import MenuNotice from '@/components/MenuNotice';
import MenuItemSkeleton from '@/components/MenuItemSkeleton';
import MenuCategorySkeleton from '@/components/MenuCategorySkeleton';
import VenueNoticeConfirmationModal from '@/components/modals/VenueNoticeBeforeCheckoutModal';
import ItemModal from '@/components/modals/ItemModal';
import VenueHeaderNew from '@/components/VenueHeaderNew';
import CrossSellModal from '@/components/modals/CrossSellModal';
import MenuOffersSkeleton from '@/components/MenuOffersSkeleton';
import MenuOffers from '@/components/MenuOffers';
import RewardsModal from '@/components/modals/RewardsModal';
import DietaryFilters from '@/components/DietaryFilters';
// import MenuRecommendations from '@/components/Recommendations.vue';
import MenuSummaryModal from '@/components/modals/MenuSummaryModal';
import isMobile from '@/helpers/isMobileDevice';
import AllergenModal from '@/components/modals/AllergenModal';
import * as menuService from '@/services/menu.service.js';
import PopularProducts from '@/components/PopularProducts';
import ModifierExtraInfoModal from '@/components/modals/ModifierExtraInfoModal';
const CategoryGrid = () => ({
    component: import('@/components/CategoryGrid')
});
import AllergenFiltersModal from '@/components/modals/AllergenFiltersModal';
const StoriesContainer = () => ({
    component: import('@/components/stories/StoriesContainer'),
    timeout: 10000
});
import storyCtaHandler from '@/mixins/storyCtaHandler';
import {
    filterCategoriesByAllergens,
    filterCategoriesByDiet
} from '@/helpers/allergens';
import BackIcon from '@/components/icons/BackIcon';

export default {
    name: 'MenuView',
    mixins: [storyCtaHandler],
    head() {
        return {
            title: this.$t('meta.menu'),
            titleTemplate: this.metaTitle || this.name + ' | %s',
            meta: [
                { name: 'twitter:card', content: 'summary_large_image' },
                { name: 'twitter:title', content: this.metaTitle || this.name },
                {
                    name: 'twitter:description',
                    content: this.metaDescription || this.description
                },
                {
                    name: 'twitter:image',
                    content: this.headerImage || this.logo
                },
                { property: 'og:title', content: this.metaTitle || this.name },
                {
                    property: 'og:description',
                    content: this.metaDescription || this.description
                },
                { property: 'og:type', content: 'website' },
                { property: 'og:image', content: this.headerImage || this.logo }
            ]
        };
    },
    beforeRouteEnter(to, from, next) {
        if (to.name === 'scan') {
            next(vm =>
                vm.$nextTick(() => {
                    vm.$modal.show('custom-message-modal', {
                        text: 'tableScanRequired',
                        clickToClose: false
                    });
                })
            );
        }

        if (from.name === 'orderStatus' && to.params.venueSlug) {
            next(vm =>
                vm.$store.dispatch('venue/getVenue', to.params.venueSlug)
            );
        }

        next();
    },
    beforeRouteUpdate(to, from, next) {
        if (from.hash === '#product') {
            this.$modal.hide('item-modal');
        }

        next();
    },
    beforeRouteLeave(to, from, next) {
        if (!this.showModalBeforeCheckout || to.name !== 'checkout') {
            next();
        }

        if (from.name !== 'basket' && to.name === 'checkout') {
            this.$modal.show('venue-notice-before-checkout-modal');
        }
    },
    components: {
        BackIcon,
        MenuOffers,
        MenuOffersSkeleton,
        VenueHeader,
        MenuItem,
        // MenuRecommendations,
        MenuStickyHeader,
        ItemModal,
        StoreNotice,
        MenuNotice,
        MenuItemSkeleton,
        MenuCategorySkeleton,
        VenueNoticeConfirmationModal,
        VenueHeaderNew,
        CrossSellModal,
        RewardsModal,
        DietaryFilters,
        MenuSummaryModal,
        AllergenModal,
        PopularProducts,
        ModifierExtraInfoModal,
        AllergenFiltersModal,
        StoriesContainer,
        CategoryGrid
    },
    computed: {
        ...mapState('venue', {
            accountId: state => state.venue.accountId,
            isLoading: state => state.isLoading,
            enabled: state => state.venue.enabled,
            busyMessage: state => state.venue.busyMessage,
            customSections: state => state.venue.customSections,
            name: state => state.venue.name,
            description: state => state.venue.description,
            metaTitle: state => state.venue.metaTitle,
            metaDescription: state => state.venue.metaDescription,
            headerImage: state => state.venue.headerImage,
            logo: state => state.venue.logo,
            visualMenu: state => state.venue.visualMenu,
            venueId: state => state.venue.id,
            autoAppliedDiscountCode: state =>
                state.venue.autoAppliedDiscountCode,
            isInStore: state => state.venue.acceptsInStore,
            largeImages: state => state.venue.largeImages,
            offers: state => state.venue.offers,
            notices: state => state.venue.venueNotices,
            crossSellCategory: state => state.venue.crossSellCategory,
            upsellProducts: state => state.venue.upsellProducts,
            showTableSelectionModal: state =>
                state.venue.showTableSelectionModal,
            showCurrencySymbol: state => state.venue.showCurrencySymbol,
            menuNotice: state => state.venue.menuNotice,
            imageSize: state => state.venue.imageSize,
            branding: state => state.venue.branding,
            allergensDisableOrdering: state =>
                state.venue.allergensDisableOrdering,
            requireTableId: state => state.venue.requireTableId
        }),
        ...mapState('menu', {
            categories: state => state.categories,
            menuIsLoading: state => state.isLoading
        }),
        ...mapState({
            cartItems: state => state.cart.items,
            selectedTableId: state => state.cart.selectedTableId
        }),
        ...mapGetters({
            fulfillmentMethod: 'cart/fulfillmentMethod',
            activeFilterTags: 'helpers/activeFilterTags',
            isStoreDesigner: 'helpers/isStoreDesigner',
            billId: 'bill/id',
            allergenDisabled: 'customer/allergenDisabled',
            isPatEnabled: 'venue/isPatEnabled',
            selectedArea: 'venue/selectedArea',
            venueValue: 'venue/venueValue',
            allergenFilters: 'helpers/allergenFilters',
            dietFilters: 'helpers/dietFilters',
            cartItemQuantity: 'cart/cartItemQuantity',
            productsMap: 'menu/productsMap'
        }),
        hasNewHeader() {
            return (
                this.isPatEnabled ||
                this.$growthbook.isOn('new_header') ||
                this.venueValue('headerStyle') === HeaderStylesEnum.MINIMAL
            );
        },
        hasStoriesEnabled() {
            return this.$growthbook.isOn('stories');
        },
        hasRecommendationsEnabled() {
            return this.$growthbook.isOn('shaped_recs');
        },
        hasQuickAddToCart() {
            return this.$growthbook.isOn('quick_add_to_cart');
        },
        shouldShowTableSelectionModal() {
            return this.showTableSelectionModal || !this.$route.query.tableId;
        },
        products() {
            return this.categories.flatMap(category =>
                category.products.map(product => ({
                    ...product,
                    categoryId: category.id,
                    categoryName: category.name,
                    categoryPosId: category.deliverectId || category.posId
                }))
            );
        },
        popularProducts() {
            return this.products
                .filter(product => product.popular)
                .map(product => {
                    return {
                        ...product,
                        isAvailable: this.isInStock(product),
                        largeImages: false,
                        imageSize: '10',
                        quickAdd: this.hasQuickAddToCart
                    };
                });
        },
        isSearchEnabled() {
            return this.products && this.products.length > 20;
        },
        hasCateringFulfillmentMethodModal() {
            if (
                this.$store.state.venue.venue.id === 9133 ||
                this.$store.state.venue.venue.id === 9469
            ) {
                return true;
            }
            return this.$growthbook.isOn('catering_fulfillment_method_modal');
        },
        hasCategoryHeaderImages() {
            return this.$growthbook.isOn('category_header_images');
        },
        isHappyEndings() {
            return this.accountId === 'b1d47448-139d-4955-afa1-72e199880113';
        },
        isPizzaPilgrims() {
            return (
                this.$store.state.venue?.venue?.accountId ===
                '24690f18-fd52-4b6c-a488-f22dd2539b88'
            );
        },
        isMenuBgImage() {
            return this.branding?.menuBgImage;
        },
        isMobile() {
            return isMobile();
        },
        hasMenuSummaryModal() {
            return this.$growthbook.isOn('menu_summary_modal');
        },
        filteredCategories() {
            // Short-circuit if not in a table service venue or if basic conditions aren't met
            if (
                !this.$store.state.venue?.venue?.acceptsInStore ||
                !this.categories ||
                !this.selectedArea
            ) {
                let categories = this.categories;

                categories = filterCategoriesByAllergens(
                    categories,
                    this.allergenFilters
                );

                categories = filterCategoriesByDiet(
                    categories,
                    this.dietFilters
                );

                return categories;
            }

            let categories = menuService.filterVisibleCategories(
                this.categories,
                this.selectedArea
            );

            categories = filterCategoriesByAllergens(
                categories,
                this.allergenFilters
            );

            categories = filterCategoriesByDiet(categories, this.dietFilters);

            return categories;
        },
        isBaoDineIn() {
            const BAO_VENUES = [9713, 9712, 9711, 9710, 9708, 8376, 9714];
            return BAO_VENUES.includes(this.venueId);
        },
        isGetBaked() {
            //get baked by account id
            return (
                this.$store.state.venue?.venue?.accountId ===
                'b2966ffd-dffc-4f6b-bc61-6bac1ee404d7'
            );
        },
        hasPopularItemsCategory() {
            return this.$growthbook.isOn('popular_items_cat');
        },
        hasActiveFilters() {
            return (
                this.activeFilterTags.length > 0 ||
                this.allergenFilters.length > 0 ||
                this.dietFilters.length > 0
            );
        },
        crossSellProducts() {
            if (this.upsellProducts && this.upsellProducts.length) {
                return this.upsellProducts
                    .map(id => this.products.find(product => product.id === id))
                    .filter(Boolean);
            }
            return this.categories
                .filter(c => c.id === this.crossSellCategory)
                .flatMap(c => c.products);
        },
        hasCategoryRouting() {
            return this.venueValue('menuView') === CategoryRoutingEnum.PAGES;
        },
        isOnCategoryPage() {
            return this.$route.name === 'menuCategory';
        },
        currentCategoryId() {
            return this.$route.params.categoryId
                ? parseInt(this.$route.params.categoryId, 10)
                : null;
        },
        currentCategory() {
            if (!this.currentCategoryId || !this.filteredCategories) {
                return null;
            }
            return this.filteredCategories.find(
                cat => cat.id === this.currentCategoryId
            );
        },
        shouldShowCategoryGrid() {
            return (
                this.hasCategoryRouting &&
                !this.isOnCategoryPage &&
                !this.search
            );
        },
        displayCategories() {
            // If user is searching, always show search results, regardless of route
            if (this.isSearchEnabled && this.search) {
                return this.categoriesSearch;
            }

            // Otherwise, if we're on a category page, show just that category
            if (this.isOnCategoryPage && this.currentCategory) {
                return [this.currentCategory];
            }

            // Fallback: normal filtered categories
            return this.filteredCategories;
        },
        navCategories() {
            return menuService.filterVisibleCategories(
                this.categories,
                this.selectedArea,
                {
                    excludeHidden: true,
                    parentOnly: this.$growthbook.isOn('sub_categories')
                }
            );
        }
    },
    watch: {
        categories() {
            if (this.$route.name === 'basket' && this.$route.params.products) {
                this.prefillCart(this.$route.params.products);
                this.$router.replace({
                    name: 'menu',
                    query: this.$route.query
                });
            }
        },
        products() {
            this.initFuse();
        },
        selectedTableId() {
            if (this.isPatEnabled && !this.billId && this.selectedTableId) {
                this.updateAndFetchBill({
                    venueId: this.venueId,
                    tableId: this.selectedTableId
                });
            }

            // When table selection changes, re-run search filtering if search is active
            if (this.search && this.search.trim() !== '') {
                this.fuseSearch();
            }
        },
        allergenFilters() {
            if (this.search && this.search.trim() !== '') {
                this.fuseSearch();
            }
        },
        filteredCategories() {
            if (!this.search || this.search.trim() === '') {
                this.categoriesSearch = this.filteredCategories;
            }
        },
        menuIsLoading: {
            handler(value) {
                if (!value && this.categories.length) {
                    const categoryId = this.$route.hash?.replace('#', '');

                    if (categoryId) {
                        this.$nextTick(() => {
                            this.scrollToElement(categoryId);
                        });
                    }
                }
            },
            immediate: true
        }
    },
    data() {
        return {
            showBasket: false,
            search: '',
            categoriesSearch: [],
            showModalBeforeCheckout: false,
            fuse: null,
            fuseSearchTimeout: null,
            hasCheckedAllergens: false,
            billPayChannel: null
        };
    },
    async mounted() {
        if (this.isStoreDesigner) {
            this.enableMessageReceive();
            this.sendMessage({ type: 'syncSettings' });
        }

        if (this.isMobile && this.hasMenuSummaryModal) {
            this.$modal.show('menu-summary-modal');
        }

        if (this.$route.name !== 'scan') {
            if (
                this.isInStore &&
                this.requireTableId &&
                !this.selectedTableId &&
                !this.$route.query.tableId
            ) {
                this.$router.push({ name: 'scan' });
            }
        }

        if (
            !this.$route.query.redirectResult &&
            this.shouldShowTableSelectionModal
        ) {
            this.triggerTableSelectionModal();
        }

        if (
            !this.$store.state.cart.fulfillmentMethod &&
            !this.$store.state.venue.venue.acceptsInStore
        ) {
            if (this.hasCateringFulfillmentMethodModal) {
                return this.triggerCateringFulfillmentMethodModal();
            }

            this.triggerFulfillmentMethodModal();
        }

        this.setContactInformation();

        getRWGToken(this.$store.state.venue.venue.slug);
        this.initFuse();

        if (this.allergensDisableOrdering) {
            // const venueId = this.$store.state.venue.venue.id;
            let allergenResponse = null;

            // // Check localStorage if available
            // if (isLocalStorageAvailable()) {
            //     allergenResponse = localStorage.getItem(
            //         `sk_allergen_disabled_${venueId}`
            //     );
            // } else {
            // If localStorage is not available, use Vuex state
            allergenResponse = this.$store.state.customer.allergenDisabled;
            // }

            // Only show modal if user hasn't responded yet (response is null)
            if (allergenResponse === null) {
                this.$modal.show('allergen-modal');
            }
        }

        if (this.isPatEnabled && !this.billId && this.selectedTableId) {
            await this.updateAndFetchBill({
                venueId: this.venueId,
                tableId: this.selectedTableId
            });
        }

        if (this.isPatEnabled && this.selectedTableId) {
            this.billPayChannel = ably.channels.get(
                `bill-payment-${this.selectedTableId}`
            );

            await this.billPayChannel.subscribe(
                `bill-payment-event-${this.selectedTableId}`,
                ({ data }) => {
                    this.handleBillUpdate(data);
                }
            );
        }
    },
    created() {
        if (this.$route.params.showBasket) {
            this.showBasket = this.$route.params.showBasket;
        }

        this.categoriesSearch = this.filteredCategories;
    },
    beforeDestroy() {
        if (this.isStoreDesigner) {
            this.disableMessageReceive();
        }

        if (this.billPayChannel) {
            this.billPayChannel.unsubscribe();
        }
    },
    methods: {
        ...mapActions({
            triggerFulfillmentMethodModal:
                'venue/triggerFulfillmentMethodModal',
            triggerCateringFulfillmentMethodModal:
                'venue/triggerCateringFulfillmentMethodModal',
            triggerTableSelectionModal: 'venue/triggerTableSelectionModal',
            prefillCart: 'cart/prefillCart',
            updateAndFetchBill: 'bill/updateAndFetchBill',
            fetchBill: 'bill/fetchBill',
            enableMessageReceive: 'storeDesigner/enableMessageReceive',
            disableMessageReceive: 'storeDesigner/disableMessageReceive',
            sendMessage: 'storeDesigner/sendMessage',
            setActiveFilterTags: 'helpers/setActiveFilterTags',
            setAllergenFilters: 'helpers/setAllergenFilters',
            setDietFilters: 'helpers/setDietFilters'
        }),
        ...mapMutations('customer', [
            'setFirstName',
            'setLastName',
            'setEmail',
            'setPhone'
        ]),
        ...mapMutations('deliveryAddress', ['setPostCode', 'setCity']),
        ...mapMutations('venue', ['SET_VISUAL_MENU']),
        ...mapMutations('bill', ['setBill']),
        onConfirmModalBeforeCheckout() {
            this.$modal.hide('venue-notice-before-checkout-modal');
            this.showModalBeforeCheckout = false;
            this.$router.push({ name: 'checkout' });
        },
        orderMenuItems(val) {
            return this.search ? val : orderBy(val, 'index');
        },
        showItemModal(item) {
            if (this.isInStock(item)) {
                this.$modal.show('item-modal', { item: item });
            }
        },
        displayCategoryDiscount(categoryId) {
            if (!this.autoAppliedDiscountCode) {
                return;
            }

            const discountCode = this.autoAppliedDiscountCode.find(code =>
                code.categories?.includes(categoryId)
            );
            if (!discountCode || !this.isCategoryDiscountValid(discountCode))
                return;

            return this.$t('menu.categoryDiscountOff', {
                value: `${discountCode.codeAmount / 10}%`
            });
        },
        isCategoryDiscountValid(discountCode) {
            // @TODO show fixed amount & BOGO discounts
            const isTypePercent =
                discountCode.type === DiscountTypesEnum.PERCENT;
            const isWithinUsageLimit =
                discountCode.usageLimit === 0 ||
                discountCode.usageCount < discountCode.usageLimit;
            const isTimeValid =
                this._isDiscountAvailableAtThisTime(discountCode);

            return isTypePercent && isWithinUsageLimit && isTimeValid;
        },
        _isDiscountAvailableAtThisTime(discountCode) {
            const { startTime, endTime } = discountCode;
            if (!startTime || !endTime) return true;

            const currentTime = this.$dayjs();
            const dateFormat = 'HH:mm:ss';
            const startMoment = this.$dayjs(startTime, dateFormat);
            const endMoment = this.$dayjs(endTime, dateFormat);

            return (
                currentTime.isAfter(startMoment) &&
                currentTime.isBefore(endMoment)
            );
        },
        isInStock(item) {
            if (item.snooze) return false;
            if (item.isAvailable && !item.trackInventory) return true;
            if (!item.isAvailable && !item.trackInventory) return false;

            if (item.trackInventory) {
                const cartQuantity = this.cartItemQuantity(item.id);
                return item.inventory - cartQuantity > 0;
            }
            return false;
        },
        updateSearchQuery(value) {
            this.search = value;
            this.fuseSearch();
        },
        searchResults(results) {
            this.categoriesSearch = processSearchResults(
                results,
                this.categories,
                this.activeFilterTags,
                this.$store.state.venue?.venue?.acceptsInStore &&
                    this.selectedArea
                    ? this.selectedArea
                    : null,
                this.allergenFilters,
                this.dietFilters
            );
        },
        resetSearchQuery() {
            this.$refs.menuStickyHeader.cancelSearch();
            this.search = ' ';
        },
        showItemModalById(itemId) {
            const id = Number(itemId);
            const item = this.productsMap?.[id];

            if (!item) {
                return;
            }

            if (!this.isInStock(item)) {
                return this.$modal.show('offer-unavailable-modal');
            }

            this.$modal.show('item-modal', { item });
        },
        setContactInformation() {
            const {
                firstName,
                lastName,
                email,
                phone,
                postCode,
                ...remainingParams
            } = this.$route.query;

            if (!firstName && !lastName && !email && !phone && !postCode) {
                return;
            }

            const setValue = (value, setter) => {
                if (value) {
                    setter(value);
                }
            };

            setValue(firstName, this.setFirstName);
            setValue(lastName, this.setLastName);
            setValue(email, this.setEmail);
            setValue(phone, this.setPhone);
            setValue(postCode, this.setPostCode);

            this.$router.replace({ query: remainingParams });
        },
        initFuse() {
            if (!this.isSearchEnabled) {
                return;
            }

            this.fuse = createSearchInstance(this.products);
            this.fuseSearch();
        },
        fuseSearch() {
            const query = this.search.trim();

            if (this.fuseSearchTimeout) {
                clearTimeout(this.fuseSearchTimeout);
            }

            const results = performSearch(this.fuse, query, this.products);

            if (query !== '') {
                this.fuseSearchTimeout = trackSearchAnalytics(
                    query,
                    results?.length,
                    this.$analytics
                );
            }

            this.searchResults(results);
        },
        scrollToElement(id) {
            const element = document.getElementById(id);

            if (!element || !element.offsetTop) {
                return;
            }

            window.scrollTo({
                top: element.offsetTop - 25,
                behavior: 'smooth'
            });
        },
        clearAllFilters() {
            this.setActiveFilterTags(null);
            this.setAllergenFilters([]);
            this.setDietFilters([]);
        },
        async handleBillUpdate({ type, billId }) {
            switch (type) {
                case 'new-bill':
                case 'bill-updated':
                    if (billId) {
                        try {
                            await this.fetchBill({
                                venueId: this.venueId,
                                billId
                            });
                        } catch (error) {
                            console.error('Failed to fetch bill:', error);
                        }
                    }
                    break;
                case 'bill-closed':
                    this.setBill(null);
                    break;
                default:
                    break;
            }
        },
        onCategoryClick(category) {
            if (this.hasCategoryRouting) {
                this.$router.push({
                    name: 'menuCategory',
                    params: { categoryId: category.id }
                });
            } else {
                this.scrollToElement(`category-${category.id}`);
            }
        },
        goBackToCategories() {
            this.$router.push({ name: 'menu' });
        }
    }
};
</script>

<style scoped src="@/assets/css/menu_scoped.css"></style>
